import { useEffect } from 'react'
import * as THREE from 'three/webgpu'

// The scene-shaped half of the material conversion.
//
// useNodeMaterials next door handles the { materials } object useGLTF
// returns. Models that arrive as a whole SCENE (the clock tower, the junk
// heap, the food set, the piano) each grew their own traverse + toNode
// copy instead, and the 31 Aug audit counted four: three byte-alike bar
// the roughness fallback, and the piano's quietly missing the material
// ARRAY branch, which is the exact bug the other three's comments warn
// about (one mesh, nine materials, half a building unconverted). One
// hook now; the divergence goes with the copies.
//
// Explicit fields rather than copy(): copy() across material classes
// drags incompatible internals over and is the idiom this codebase has
// banned everywhere else.

export function toNode(src, cache, roughness = 0.92) {
  if (!src) return src
  if (src.isNodeMaterial) return src
  const hit = cache.get(src)
  if (hit) return hit
  const nm = new THREE.MeshStandardNodeMaterial()
  if (src.map) nm.map = src.map
  if (src.aoMap) nm.aoMap = src.aoMap
  nm.color = src.color?.clone() ?? new THREE.Color('white')
  nm.roughness = src.roughness ?? roughness
  nm.metalness = src.metalness ?? 0
  nm.side = src.side
  nm.transparent = src.transparent
  nm.opacity = src.opacity ?? 1
  nm.alphaTest = src.alphaTest ?? 0
  nm.vertexColors = src.vertexColors
  cache.set(src, nm)
  return nm
}

/**
 * Convert every mesh material in a GLB scene to a node material, hide the
 * stray line primitives exporters leave behind, and set shadow flags.
 *
 * @param {THREE.Object3D} scene - from useModel/useGLTF
 * @param {object} [opts]
 * @param {number} [opts.roughness=0.92] - fallback when the source has none
 * @param {boolean} [opts.shadows=true] - cast/receive on every mesh
 */
export function useSceneNodeMaterials(scene, { roughness = 0.92, shadows = true } = {}) {
  useEffect(() => {
    if (!scene) return
    const converted = new Map()
    scene.traverse((o) => {
      if (o.isLine || o.isLineSegments) { o.visible = false; return }
      if (!o.isMesh) return
      if (shadows) { o.castShadow = true; o.receiveShadow = true }
      // One mesh, nine materials: it reaches three as an array, and
      // skipping arrays is how half a building ends up unconverted.
      o.material = Array.isArray(o.material)
        ? o.material.map((m) => toNode(m, converted, roughness))
        : toNode(o.material, converted, roughness)
    })
  }, [scene, roughness, shadows])
}

export default useSceneNodeMaterials
